// Landing page for adhoc display — ported from ref-landing-page/mywellfie-landing_1.html

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Brain, Check, Heart, MailCheck, Play, ScanFace, Smartphone, UserRound, Wallet, Zap } from 'lucide-react';
import navLogoSrc from '../../assets/mywellfie-header-logo.png';
import reportLogoSrc from '../../assets/mywellfie-full-logo.png';
import { ADHOC_SCAN_MAINTENANCE_MESSAGE } from '../../config/featureFlags';
import './landing.css';

export interface AdhocLandingOffer {
  priceDisplay: string;
  compareAtDisplay: string;
  discountLabel: string;
}

export interface AdhocLandingStageProps extends AdhocLandingOffer {
  adhocId: string;
  scanCreditsRemaining?: number | null;
  onOpenMyScanCredits?: () => void;
  singleScanHref?: string;
  multiScanHref?: string;
  error: string | null;
  /** When true, new checkout links are disabled (existing pack credits may still open hub). */
  scanMaintenance?: boolean;
}

function multiScanPath(adhocId: string): string {
  return `/display/family-pack?adhoc_id=${encodeURIComponent(adhocId)}`;
}

function singleScanPath(adhocId: string): string {
  return `/display/checkout?adhoc_id=${encodeURIComponent(adhocId)}`;
}

function countTo(
  el: HTMLElement,
  target: number,
  suffix: string,
  duration: number,
  reduced: boolean,
) {
  if (reduced) {
    el.textContent = `${target}${suffix}`;
    return;
  }
  const start = performance.now();
  function tick(now: number) {
    const p = Math.min((now - start) / duration, 1);
    const eased = 1 - (1 - p) ** 3;
    const val = Number.isInteger(target)
      ? Math.round(eased * target)
      : (eased * target).toFixed(1);
    el.textContent = `${val}${suffix}`;
    if (p < 1) requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

const SingleScanLink: React.FC<{
  href: string;
  className?: string;
  children?: React.ReactNode;
}> = ({ href, className = 'btn btn-offer', children }) => (
  <Link to={href} className={className}>
    {children ?? (
      <>
        Get my scan
        <span className="btn-arrow" aria-hidden>→</span>
      </>
    )}
  </Link>
);

const MultiScanLink: React.FC<{
  href: string;
  className?: string;
  label?: string;
}> = ({ href, className = 'btn btn-nav-offer', label = 'Buy multiple scans' }) => (
  <Link to={href} className={className}>
    {label}
    <span className="btn-arrow" aria-hidden>→</span>
  </Link>
);

const LandingCtaStack: React.FC<{
  offer: AdhocLandingOffer;
  singleScanHref: string;
  multiScanHref: string;
  centered?: boolean;
  primaryClassName?: string;
  scanCreditsRemaining?: number | null;
  onOpenMyScanCredits?: () => void;
  journeyPrimary?: boolean;
  scanCheckoutDisabled?: boolean;
}> = ({
  offer,
  singleScanHref,
  multiScanHref,
  centered = false,
  primaryClassName = 'btn btn-offer',
  scanCreditsRemaining,
  onOpenMyScanCredits,
  journeyPrimary = false,
  scanCheckoutDisabled = false,
}) => {
  const hasCredits = scanCreditsRemaining != null
    && scanCreditsRemaining > 0
    && Boolean(onOpenMyScanCredits);
  const remaining = hasCredits ? (scanCreditsRemaining as number) : 0;

  const primaryArrow = journeyPrimary ? (
    <Zap size={18} strokeWidth={2.4} fill="currentColor" aria-hidden />
  ) : (
    <span className="btn-arrow" aria-hidden>→</span>
  );

  return (
    <div className={`cta-offer-stack${centered ? ' cta-offer-stack-center' : ''}`}>
      <div className="cta-primary-wrap">
        {hasCredits ? (
          <button
            type="button"
            className={primaryClassName}
            onClick={onOpenMyScanCredits}
          >
            Start my scan
            {primaryArrow}
          </button>
        ) : scanCheckoutDisabled ? (
          <button
            type="button"
            className={primaryClassName}
            disabled
            style={{ opacity: 0.65, cursor: 'not-allowed' }}
          >
            Checkout paused — under maintenance
          </button>
        ) : journeyPrimary ? (
          <SingleScanLink href={singleScanHref} className={primaryClassName}>
            Get 1 scan — {offer.priceDisplay}
            {primaryArrow}
          </SingleScanLink>
        ) : (
          <SingleScanLink href={singleScanHref} className={primaryClassName}>
            Get my scan
            {primaryArrow}
          </SingleScanLink>
        )}
        {hasCredits && (
          <p className="scan-credits-remaining">
            {remaining} scan{remaining === 1 ? '' : 's'} remaining
          </p>
        )}
      </div>
      {scanCheckoutDisabled ? (
        <button
          type="button"
          className="btn btn-nav-offer"
          disabled
          style={{ opacity: 0.65, cursor: 'not-allowed' }}
        >
          Buy multiple scans (paused)
        </button>
      ) : (
        <MultiScanLink href={multiScanHref} />
      )}
    </div>
  );
};

const JourneyCtaCard: React.FC<{
  offer: AdhocLandingOffer;
  singleScanHref: string;
  multiScanHref: string;
  scanCreditsRemaining?: number | null;
  onOpenMyScanCredits?: () => void;
  scanCheckoutDisabled?: boolean;
}> = ({
  offer, singleScanHref, multiScanHref, scanCreditsRemaining, onOpenMyScanCredits,
  scanCheckoutDisabled = false,
}) => (
  <article className="journey-card">
    <div className="journey-card-head">
      <h2>Start Your Wellness Journey Today</h2>
      <p className="journey-card-subhead">
        Experience the future of health monitoring for a fraction of the cost.
      </p>
    </div>

    <div className="journey-offer-box">
      <span className="journey-offer-label">Introductory offer</span>
      <div className="journey-offer-row">
        <div className="journey-offer-pricing">
          <p className="journey-offer-price">First Scan: {offer.priceDisplay}</p>
          {offer.compareAtDisplay && (
            <p className="journey-offer-was">Normally {offer.compareAtDisplay} per scan</p>
          )}
        </div>
        {offer.discountLabel && (
          <span className="journey-save-badge">
            Save {offer.discountLabel.replace(/\s*OFF\s*$/i, '')} Today
          </span>
        )}
      </div>
    </div>

    <div className="journey-card-cta">
      <LandingCtaStack
        offer={offer}
        singleScanHref={singleScanHref}
        multiScanHref={multiScanHref}
        centered
        primaryClassName="btn btn-journey"
        scanCreditsRemaining={scanCreditsRemaining}
        onOpenMyScanCredits={onOpenMyScanCredits}
        journeyPrimary
        scanCheckoutDisabled={scanCheckoutDisabled}
      />
    </div>

    <p className="journey-fine">
      Secure checkout via Stripe or HitPay. Full health report emailed after your scan.
    </p>
  </article>
);

const HOW_STEPS = [
  {
    index: '01',
    title: 'Tap to Start',
    body: 'Tap the offer on the booth screen to pay securely and begin your scan session.',
    Icon: Wallet,
  },
  {
    index: '02',
    title: 'Quick Profile',
    body: 'Enter your name and email so we can personalize your report and send it to you.',
    Icon: UserRound,
  },
  {
    index: '03',
    title: 'Face Scan',
    body: 'Center your face in the guide and hold still while our PPG technology reads blood flow.',
    Icon: ScanFace,
  },
  {
    index: '04',
    title: 'Instant Results',
    body: 'View your vitals on screen and receive a full health report in your inbox within seconds.',
    Icon: MailCheck,
  },
] as const;

const SCIENCE_POINTS = [
  {
    title: 'Medical-Grade Accuracy',
    body: 'Used in hospital pulse oximeters for decades.',
  },
  {
    title: 'AI-Driven Extraction',
    body: 'Our algorithms filter noise to extract pure biometric signals.',
  },
  {
    title: 'Rapid Processing',
    body: 'Massive data sets processed in under 60 seconds.',
  },
] as const;

const SciencePpgVisual: React.FC = () => (
  <div
    className="science-media"
    role="img"
    aria-label="Illustration of photoplethysmography optical signals captured from skin"
  >
    <svg className="science-media-svg" viewBox="0 0 560 360" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <linearGradient id="science-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0A1218" />
          <stop offset="55%" stopColor="#101E28" />
          <stop offset="100%" stopColor="#152631" />
        </linearGradient>
        <linearGradient id="science-beam" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="#3F9C8C" stopOpacity="0" />
          <stop offset="45%" stopColor="#3F9C8C" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#7FE0C4" stopOpacity="0.85" />
        </linearGradient>
        <linearGradient id="science-ground" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1B3340" />
          <stop offset="100%" stopColor="#0E1820" />
        </linearGradient>
      </defs>
      <rect width="560" height="360" fill="url(#science-sky)" />
      <rect x="0" y="248" width="560" height="112" fill="url(#science-ground)" />
      {[40, 95, 160, 230, 310, 390, 470, 520].map((x) => (
        <circle key={x} cx={x} cy={38 + (x % 70) * 0.08} r="1.2" fill="#7FE0C4" opacity="0.35" />
      ))}
      <path
        d="M120 248 Q180 210 240 248 T360 248 T480 248"
        fill="none"
        stroke="#243844"
        strokeWidth="1"
      />
      <rect x="188" y="72" width="34" height="176" rx="17" fill="url(#science-beam)" opacity="0.55" />
      <rect x="248" y="48" width="42" height="200" rx="21" fill="url(#science-beam)" opacity="0.75" />
      <rect x="318" y="86" width="30" height="162" rx="15" fill="url(#science-beam)" opacity="0.45" />
      <path
        className="science-wave"
        d="M0,300 L28,300 L36,276 L44,318 L52,264 L60,306 L74,300 L280,300 L292,272 L304,322 L316,258 L328,308 L342,300 L560,300"
        fill="none"
        stroke="#5DA73D"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        className="science-wave science-wave-dim"
        d="M0,318 L24,318 L32,302 L40,328 L48,294 L56,322 L68,318 L280,318 L288,304 L296,330 L304,292 L312,324 L324,318 L560,318"
        fill="none"
        stroke="#3F9C8C"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.55"
      />
    </svg>
    <div className="science-play" aria-hidden>
      <Play size={28} strokeWidth={2.4} fill="currentColor" />
    </div>
  </div>
);

const AdhocLandingStage: React.FC<AdhocLandingStageProps> = ({
  adhocId,
  scanCreditsRemaining,
  onOpenMyScanCredits,
  singleScanHref,
  multiScanHref,
  priceDisplay,
  compareAtDisplay,
  discountLabel,
  error,
  scanMaintenance = false,
}) => {
  const offer: AdhocLandingOffer = { priceDisplay, compareAtDisplay, discountLabel };
  const multiHref = multiScanHref ?? multiScanPath(adhocId);
  const checkoutHref = singleScanHref ?? singleScanPath(adhocId);
  const bpmRef = useRef<HTMLSpanElement>(null);
  const wellRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (bpmRef.current) countTo(bpmRef.current, 72, '', 1400, reduced);
    if (wellRef.current) countTo(wellRef.current, 8.4, '', 1400, reduced);
  }, []);

  const hasCredits = scanCreditsRemaining != null
    && scanCreditsRemaining > 0
    && Boolean(onOpenMyScanCredits);

  return (
    <div className="adhoc-landing">
      {scanMaintenance && (
        <div
          role="status"
          style={{
            background: '#FFFBEB',
            borderBottom: '1px solid #FDE68A',
            color: '#92400E',
            padding: '12px 20px',
            fontSize: 14,
            lineHeight: 1.5,
            textAlign: 'center',
          }}
        >
          <strong>Under maintenance.</strong> {ADHOC_SCAN_MAINTENANCE_MESSAGE}
        </div>
      )}
      {error && (
        <div className="pay-error" role="alert">
          {error}
        </div>
      )}

      <nav>
        <div className="wrap nav-row-split">
          <div className="nav-logo">
            <img src={navLogoSrc} alt="MyWellfie — Smart scans. Health insights." />
          </div>
          {hasCredits ? (
            <button
              type="button"
              className="btn btn-nav-offer nav-credits-continue"
              onClick={onOpenMyScanCredits}
            >
              {scanCreditsRemaining} scan{scanCreditsRemaining === 1 ? '' : 's'} · Continue
            </button>
          ) : scanMaintenance ? (
            <button
              type="button"
              className="btn btn-nav-offer"
              disabled
              style={{ opacity: 0.65, cursor: 'not-allowed' }}
            >
              Checkout paused
            </button>
          ) : (
            <SingleScanLink href={checkoutHref} className="btn btn-nav-offer">
              Get my scan
            </SingleScanLink>
          )}
        </div>
      </nav>

      <section className="hero">
        <div className="glow" />
        <div className="wrap hero-grid">
          <div className="hero-copy">
            <span className="eyebrow">Smart scans. Health insights.</span>
            <h1>
              Take a <em>wellfie.</em>
              <br />
              Get your vitals back.
            </h1>
            <p className="lede">
              Hold still for a face scan and get a full personal health report — pulse,
              blood pressure, breathing rate, stress, and key blood markers — in under a
              minute. No cuffs, clips, or lab visit.
            </p>
          </div>

          <div className="phone-stage">
            <div className="phone">
              <div className="screen">
                <svg
                  className="scan-svg"
                  viewBox="0 0 240 320"
                  xmlns="http://www.w3.org/2000/svg"
                  role="img"
                  aria-label="Animated illustration of a face scan reading vital signs"
                >
                  <ellipse cx="120" cy="118" rx="52" ry="64" fill="none" stroke="#3A5563" strokeWidth="1.5" />
                  <path
                    d="M58,250 Q120,200 182,250 L182,300 L58,300 Z"
                    fill="none"
                    stroke="#3A5563"
                    strokeWidth="1.5"
                  />
                  <path
                    className="wave"
                    d="M0,210 L26,210 L33,188 L40,228 L47,178 L54,216 L68,210 L120,210 L127,188 L134,228 L141,178 L148,216 L162,210 L240,210"
                    fill="none"
                    stroke="#5DA73D"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <rect className="scan-line" x="0" y="0" width="240" height="3" fill="#7FE0C4" opacity="0.9" />
                </svg>
                <div className="readouts">
                  <div className="readout">
                    <span className="num" ref={bpmRef}>0</span>
                    <span className="lbl">pulse · bpm</span>
                  </div>
                  <div className="readout">
                    <span className="num" ref={wellRef}>0</span>
                    <span className="lbl">wellness</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-cta-row">
            <LandingCtaStack
              offer={offer}
              singleScanHref={checkoutHref}
              multiScanHref={multiHref}
              centered
              scanCreditsRemaining={scanCreditsRemaining}
              onOpenMyScanCredits={onOpenMyScanCredits}
              scanCheckoutDisabled={scanMaintenance}
            />
          </div>
        </div>
      </section>

      <section className="section offers-section">
        <div className="wrap">
          <div className="offers-head">
            <span className="eyebrow">What we offer</span>
            <h2>What MyWellfie Offers</h2>
            <p>Comprehensive health tracking powered by precision AI.</p>
          </div>
          <div className="offers-bento">
            <article className="offer-card offer-card-light offer-card-heart">
              <div className="offer-icon offer-icon-rose" aria-hidden>
                <Heart size={22} strokeWidth={2.2} />
              </div>
              <h3>Heart Health</h3>
              <p>
                Advanced monitoring for heart rate and Heart Rate Variability (HRV).
                Get early detection of cardiovascular trends and understand your heart&apos;s
                resilience under stress.
              </p>
              <svg className="offer-chart" viewBox="0 0 320 80" aria-hidden>
                <rect x="8" y="48" width="18" height="28" rx="4" fill="#FECACA" />
                <rect x="36" y="32" width="18" height="44" rx="4" fill="#FCA5A5" />
                <rect x="64" y="40" width="18" height="36" rx="4" fill="#F87171" />
                <rect x="92" y="24" width="18" height="52" rx="4" fill="#EF4444" />
                <rect x="120" y="36" width="18" height="40" rx="4" fill="#F87171" />
                <rect x="148" y="20" width="18" height="56" rx="4" fill="#DC2626" />
                <rect x="176" y="44" width="18" height="32" rx="4" fill="#FCA5A5" opacity="0.7" />
                <rect x="204" y="52" width="18" height="24" rx="4" fill="#FECACA" opacity="0.5" />
              </svg>
            </article>

            <article className="offer-card offer-card-dark offer-card-vitals">
              <div className="offer-icon offer-icon-sky" aria-hidden>
                <Activity size={22} strokeWidth={2.2} />
              </div>
              <h3>Vital Signs</h3>
              <p>
                Track blood pressure trends with clinical
                accuracy using only your smartphone lens.
                {/* SpO2 disabled — not available from web SDK */}
              </p>
            </article>

            <article className="offer-card offer-card-light offer-card-stress">
              <div className="offer-icon offer-icon-blue" aria-hidden>
                <Brain size={22} strokeWidth={2.2} />
              </div>
              <h3>Stress &amp; Wellness</h3>
              <p>
                Measure stress levels and systemic recovery. Understand when to push harder
                and when to focus on rest and rejuvenation.
              </p>
            </article>

            <article className="offer-card offer-card-accent offer-card-anytime">
              <div className="offer-icon offer-icon-green" aria-hidden>
                <Smartphone size={22} strokeWidth={2.2} />
              </div>
              <h3>Anytime, Anywhere</h3>
              <p>
                No external hardware or bulky wearables required. Your wellness companion
                is already in your pocket, ready whenever you are.
              </p>
              <div className="offer-accent-ring" aria-hidden />
            </article>
          </div>
        </div>
      </section>

      <section className="section how-section">
        <div className="wrap">
          <div className="how-head">
            <span className="eyebrow">How it works</span>
            <h2>How It Works</h2>
            <p>Get medical-grade insights in under 60 seconds.</p>
          </div>
          <div className="how-rail">
            <div className="how-rail-line" aria-hidden />
            <ol className="how-steps">
              {HOW_STEPS.map(({ index, title, body, Icon }) => (
                <li key={index} className="how-step">
                  <div className="how-step-marker">
                    <span className="how-step-index">{index}</span>
                    <div className="how-step-icon-wrap">
                      <Icon size={22} strokeWidth={2} aria-hidden />
                    </div>
                  </div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="section what-is">
        <div className="wrap what-grid">
          <div>
            <span className="eyebrow">What is mywellfie</span>
            <h2>Your camera, doing a full checkup.</h2>
            <p className="copy">
              MyWellfie turns the camera you already carry into a clinical-grade signal
              reader. Hold still for a face scan, and our model reads the tiny shifts in
              skin color caused by blood flow underneath — the same signal pulse oximeters
              and ECGs look for, just without the clip, cuff, or wires.
            </p>
          </div>
          <div className="facts">
            <div className="fact">
              <h3>No extra hardware</h3>
              <p>Just the phone already in your pocket. No wearables, cuffs, or sensors to buy or charge.</p>
            </div>
            <div className="fact">
              <h3>A full report in under a minute</h3>
              <p>Cardiovascular, respiratory, stress, and metabolic readings land on your profile automatically.</p>
            </div>
            <div className="fact">
              <h3>Insights tuned to your scan</h3>
              <p>Curated wellness content and corporate programs adjust to what your numbers actually show.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section science-section">
        <div className="wrap science-grid">
          <SciencePpgVisual />
          <div className="science-copy">
            <span className="eyebrow eyebrow-light">The science</span>
            <h2>
              The Science:{' '}
              <span className="science-accent">PPG Technology</span>
            </h2>
            <p className="science-lede">
              Photoplethysmography (PPG) is a clinically validated, non-invasive optical
              technique that detects blood volume changes in the microvascular bed.
            </p>
            <ul className="science-points">
              {SCIENCE_POINTS.map(({ title, body }) => (
                <li key={title}>
                  <span className="science-check" aria-hidden>
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span>
                    <strong>{title}:</strong> {body}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="section report-section">
        <div className="wrap">
          <div className="report-head">
            <span className="eyebrow">Inside your report</span>
            <h2>One scan, five systems read at once.</h2>
            <p>Every scan returns a personal health report with the same categories a check-up would cover.</p>
          </div>
          <div className="report-grid">
            <div className="rcard">
              <div className="cat"><span className="dot" style={{ background: 'var(--blue)' }} />Cardiovascular</div>
              <div className="metric"><span className="name">Pulse rate</span><span className="val">72 bpm</span></div>
              <div className="metric"><span className="name">Blood pressure</span><span className="val">118 / 76</span></div>
            </div>
            <div className="rcard">
              <div className="cat"><span className="dot" style={{ background: 'var(--teal)' }} />Respiratory</div>
              <div className="metric"><span className="name">Respiration rate</span><span className="val">16 brpm</span></div>
              {/* SpO2 disabled — web SDK
              <div className="metric"><span className="name">Oxygen saturation</span><span className="val">98%</span></div>
              */}
            </div>
            <div className="rcard">
              <div className="cat"><span className="dot" style={{ background: 'var(--green)' }} />HRV &amp; nervous system</div>
              <div className="metric"><span className="name">SDNN</span><span className="val">45 ms</span></div>
              <div className="metric"><span className="name">RMSSD</span><span className="val">42 ms</span></div>
            </div>
            <div className="rcard">
              <div className="cat"><span className="dot" style={{ background: 'var(--blue)' }} />Stress &amp; wellness</div>
              <div className="metric"><span className="name">Stress level</span><span className="val">Moderate</span></div>
              <div className="metric"><span className="name">Wellness index</span><span className="val">8.4</span></div>
            </div>
            <div className="rcard">
              <div className="cat"><span className="dot" style={{ background: 'var(--teal)' }} />Metabolic &amp; blood</div>
              <div className="metric"><span className="name">Haemoglobin</span><span className="val">14.2 g/dL</span></div>
              <div className="metric"><span className="name">HbA1c</span><span className="val">5.3%</span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="section journey-cta-section">
        <div className="wrap journey-cta-wrap">
          <JourneyCtaCard
            offer={offer}
            singleScanHref={checkoutHref}
            multiScanHref={multiHref}
            scanCreditsRemaining={scanCreditsRemaining}
            onOpenMyScanCredits={onOpenMyScanCredits}
            scanCheckoutDisabled={scanMaintenance}
          />
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="brand">
            <img src={reportLogoSrc} alt="MyWellfie" />
            {/* <span>MyWellfie</span> */}
          </div>
          <span className="fine">
            MyWellfie reports are for informational purposes only and don&apos;t replace
            professional medical advice.
          </span>
        </div>
      </footer>
    </div>
  );
};

export default AdhocLandingStage;
